import React, { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { Box, Button, Grid, makeStyles } from "@material-ui/core";
import BarChartIcon from "@material-ui/icons/BarChart";
import RefreshIcon from "@material-ui/icons/Refresh";

import Header from "../header";
import ConversionWidgets from "./components/ConversionWidgets.js";
import { getReportUserId } from "../../store/ui/report";
import UserAutocomplete from "./components/UserAutocomplete";
import UserStatusReport from "./components/UserStatusReport";
import { getUserById } from "../../store/hub/users";
import DatePicker from "./components/DatePicker";
import {
  updateMainView
} from "../../components/common/StoreCommon";

const actions = [
  {
    icon: <BarChartIcon style={{ color: "rgb(185 181 181)" }} />,
    name: "User Stats",
    selected: false,
    onClick: () => updateMainView("Team Overview"),
  },
];

export default function Stats() {
  const classes = useStyles();
  const reportUserId = useSelector(getReportUserId)();
  const reportUser = useSelector(getUserById)(reportUserId);
  const [compareUserIds, setCompareUserIds] = useState([reportUser]);
  /** Dates shown in the pickers; changing these does not fetch until Refresh. */
  const [pickerDate, setPickerDate] = useState([new Date(), null]);
  /** Dates passed to report widgets; updates only on Refresh (and matches initial picker state). */
  const [queryDate, setQueryDate] = useState([new Date(), null]);
  /** Bumps on each Refresh so reports refetch even when the date range is unchanged (e.g. retry after error). */
  const [reportRefreshNonce, setReportRefreshNonce] = useState(0);
  /** Active report API requests; Refresh stays disabled while any are in flight. */
  const [pendingReportRequests, setPendingReportRequests] = useState(0);

  const onReportLoadingChange = useCallback((loading) => {
    setPendingReportRequests((c) => (loading ? c + 1 : Math.max(0, c - 1)));
  }, []);

  const handleRefreshReports = () => {
    setQueryDate([pickerDate[0], pickerDate[1]]);
    setReportRefreshNonce((n) => n + 1);
  };

  const additionalHeaderHtml = () => {
    return (
      <Box style={{ display: "flex", flexDirection: "row", marginLeft: 20, flexWrap: "wrap", alignItems: "center" }}>
        <UserAutocomplete
          compareUserIds={compareUserIds}
          setCompareUserIds={setCompareUserIds}
        />
        <Box style={{ flexShrink: 0, minWidth: 180, marginLeft: 10, display: "flex", alignItems: "center", gap: 8 }}>
          <DatePicker value={pickerDate} setValue={setPickerDate} />
          <Button
            color="primary"
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={handleRefreshReports}
            disabled={pendingReportRequests > 0}
            style={{ fontWeight: 700 }}
          >
            Refresh
          </Button>
        </Box>
      </Box>
    );
  };

  const isSingleUser = compareUserIds && compareUserIds.length === 1;

  return (
    <Box className={isSingleUser ? classes.statsWrapper : undefined}>
      <Header actions={actions} additional={additionalHeaderHtml()} />
      <Grid container className={isSingleUser ? classes.root : classes.rootMulti}>
          {compareUserIds && compareUserIds.length > 1 ? (
            compareUserIds.map((user) => (
              <UserStatusReport
                key={user.Id}
                user={user}
                date={queryDate}
                refreshNonce={reportRefreshNonce}
                onReportLoadingChange={onReportLoadingChange}
              />
            ))
          ) : (
            <ConversionWidgets
              user={
                compareUserIds && compareUserIds.length >= 1
                  ? compareUserIds[0]
                  : reportUser
              }
              date={queryDate}
              refreshNonce={reportRefreshNonce}
              onReportLoadingChange={onReportLoadingChange}
            />
          )}
        </Grid>
    </Box>
  );
}

const useStyles = makeStyles((theme) => ({
  statsWrapper: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minHeight: 0,
    overflow: "hidden",
  },
  root: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(3),
    minHeight: 0,
    borderTop: "1px solid rgba(0, 0, 0, 0.12)",
  },
  rootMulti: {
    display: "flex",
    flexGrow: 1,
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    paddingTop: theme.spacing(4),
    minHeight: "70vh",
    borderTop: "1px solid rgba(0, 0, 0, 0.12)",
  },
}));
